import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  Modal,
  Box,
  TextField,
  Autocomplete,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  FormControlLabel,
  Icon,
  Snackbar,
  Alert, Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import TableActionMenu from "components/TableActionMenu";
import PhoneCell from "components/Softphone/PhoneCell";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import { formatDate } from 'utils/DateTimeConversion';
import ContactFormModal from 'components/NewContactModal';
import { Contact } from 'lucide-react';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import EmptyState from 'components/EmptyTable/EmptyState';
import { usePermission } from 'auth/usePermission';
import { frontendLogger } from '../../utils/logger';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import StatusBadge from "../../components/StatusBadge";
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import { useTheme } from '@mui/material/styles';
import PageHeader from 'components/v2/PageHeader';
import { getApiErrorMessage } from 'utils/apiErrors';
import { useActiveApp } from 'shell-v2/apps';

function Contacts() {
  const { user, axiosInstance } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const { canCreate, isSupportUser } = usePermission('contacts');
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // statistics row, then the table in one card.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();
  const [newContact, setNewContact] = useState({
    // title: '', // New field for title
    // first_name: '',
    // middle_name: '',
    // last_name: '',
    // email: '',
    // phone: '',
    // mobile_number: '',
    // notes: '',
    // account: null,
    // mailing_address: '',
    // mailing_country: '',
    // mailing_state: '',
    // mailing_city: '',
    // mailing_postal_code: '',
    primary_contact: false,
  });
  const [formData, setFormData] = useState({

    title: 'Mr.',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const [allContacts, setAllContacts] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [filteredContacts, setFilteredContacts] = useState([]);
  useEffect(() => {
    fetchContacts();
  }, []);

  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const buildContactDisplayName = (contact) =>
    [contact?.first_name, contact?.middle_name, contact?.last_name]
      .map((part) => capitalizeFirstLetter(part || ""))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

  const transformContactListData = (serverData) =>
    (serverData || []).map((contact) => ({
      ...contact,
      // Display aliases so list + CSV export share the same values as the cells
      name: buildContactDisplayName(contact),
      status: contact.contact_status || "Active",
      contact_status: contact.contact_status || "Active",
      account_name: contact.account_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      primary_contact: Boolean(contact.primary_contact),
    }));

  const fetchContacts = async () => {
    setIsLoading(true);
    try {
      // Match Leads/Deals: load the full list so the grid and CSV export are not capped at the default API limit (20).
      const response = await axiosInstance.get('/contacts/?all=true');
      const transformed = transformContactListData(response.data);
      setContacts(transformed);
      setFilteredContacts(transformed);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setSnackbar({ open: true, message: getApiErrorMessage(error, 'Could not load contacts. Please try again.'), severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAccounts = async (query) => {
    try {
      const response = await axiosInstance.get(`/accounts/?search=${query}`);
      setAccounts(response.data);
      console.log(`filteredAccounts:`, response.data);

    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      const delayDebounceFn = setTimeout(() => {
        fetchAccounts(searchQuery);
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchQuery]);

  // DELETE operation states

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({open: false, id: null});

  // DELETE selected Contact

  const handleDeleteClick = (id) => {setDeleteConfirm({open: true, id: id})}

  // Send DELETE request

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/contacts/${deleteConfirm.id}`);
      setSnackbar({open: true, message: 'Contact deleted successfully', severity: 'success'})
      await fetchContacts();
    } catch (error) {
      setSnackbar({open: true, message: 'Error deleting Contact', severity: 'error'})
    } finally {
      setIsDeleting(false);
      setDeleteConfirm({open: false, id: null})
    }
  }

  const handleOpenModal = () => setIsModalOpen(true);
  // v2 header "New contact": the same dialog as Add Contact.
  useOpenCreateFromUrl(handleOpenModal);
  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Reset the form
    setNewContact({
      // title: '',
      // first_name: '',
      // middle_name: '',
      // last_name: '',
      // email: '',
      // phone: '',
      // mobile_number: '',
      // notes: '',
      // account: '',
      // mailing_address: '',
      // mailing_country: '',
      // mailing_state: '',
      // mailing_city: '',
      // mailing_postal_code: '',
      // account: null,
      primary_contact: false,

    });
  };

  const handleInputChange = (e) => {
    setNewContact({ ...newContact, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setNewContact({ ...newContact, primary_contact: e.target.checked });
  };

  const transformContactData = (serverData) => {
    return serverData.map(contacts => ({
      // id: lead.id,
      // name: lead.first_name + ' ' + lead.last_name || "",
      // company: lead.company_name || "",
      // source: lead.lead_source || "",
      // email: lead.email || "",
      // status: lead.lead_status || "",
      // updated_at: lead.updated_at?.split('T')[0] || "",
      // lead_rating: "7.4" || "",  // TODO: Replace with real data
      // owner_name: lead.owner?.name || "",
      // owner_avatar: lead.owner?.avatar || "https://via.placeholder.com/24",
      // assigned_to_name: lead.assigned_to_name || "Unassigned",
      // assigned_to: lead.assigned_to,
      created_by_name: contacts?.created_by_name || "",
      updated_by_name: contacts?.updated_by_name || "",
      // last_activity: lead?.last_activity?.split('T')[0] || "",
      last_activity: formatDate(contacts?.last_activity, 'datetime') || "",
    }));
  };

  const handleSaveContact = async (contactData) => {
    setIsLoading(true);

    // Check if the user is a support user
    if (isSupportUser) {
      setIsLoading(false);
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create contacts.',
        severity: 'error',
      });
      return; // Exit the function early
    }

    try {
      const contactPayload = {
        ...contactData,
        // account_id: contactData.account ? contactData.account.id : null, // Ensure account_id is being passed 
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
      };
      console.log(`contactPayload for handleSaveContact:`, contactData.account);

      const response = await axiosInstance.post('/contacts/', contactPayload);
      handleCloseModal();
      setAllContacts(prevContacts => [...prevContacts, transformContactData([response.data])[0]]); // Assuming you have setAllContacts like setAllLeads
      await fetchContacts();
      navigate(`/contacts/${response.data.id}`);
      // Best-effort: a logging failure shouldn't surface as a creation error
      // or invite a retry — the contact already exists at this point.
      try {
        await frontendLogger.logCreate('Contact', {
          id: response.data.id,
          user: user.username,
          details: { ...contactPayload, user: user.username }
        });
      } catch (logError) {
        console.error('Error logging contact creation:', logError);
      }
    } catch (error) {
      console.error('Error creating new contact:', error);
      const detail = error.response?.data?.detail;
      setSnackbar({
        open: true,
        message: typeof detail === 'string' ? detail : 'Error creating contact. Please try again.',
        severity: 'error',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };




  const renderAccountAutocomplete = () => (
    <Autocomplete
      options={accounts}
      getOptionLabel={(option) => option.name || ''}
      onInputChange={(e, newInputValue) => setSearchQuery(newInputValue)}
      onChange={(e, selectedAccount) => {
        console.log('Selected account:', selectedAccount); // Debug log
        setNewContact({ ...newContact, account: selectedAccount });
      }}
      value={newContact.account}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Account"
          variant="outlined"
          margin="normal"
        />
      )}
    />
  );
  const handleRowClick = (rowData) => {
    navigate(`/contacts/${rowData.id}`);
  };

  const contactsTableData = useMemo(() => ({
    columns: [
      {
        Header: "Contact Name",
        accessor: "name",
        width: "15%",
        Cell: ({ row }) => (
          <MDTypography
            component="span"
            onClick={() => handleRowClick(row.original)}
            variant="caption"
            color="info"
            fontWeight="medium"
            sx={{
              cursor: 'pointer',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            {row.original.name || buildContactDisplayName(row.original)}
          </MDTypography>
        )
      },
      {
        Header: "Account",
        accessor: "account_name",
        width: "15%",
        Cell: ({ row }) => {
          const accountId = row.original.account_id;
          const accountName = row.original.account_name;
          return accountName ? (
              <MDTypography
                  variant="caption"
                  color="info"
                  fontWeight="medium"
                  sx={{
                    cursor: 'pointer',
                    color: 'info',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    lineHeight: 1.35,
                    display: 'inline-block',
                    maxWidth: '100%',
                  }}
                  onClick={() => navigate(`/accounts/${accountId}`)}
              >
                {capitalizeFirstLetter(accountName)}
              </MDTypography>
          ) : (
              <MDTypography variant="caption" color="text">
                {/* No Account */}
              </MDTypography>
          );
        }
      },
      {
        Header: "Email",
        accessor: "email",
        width: "30%",
        // Keep the real email casing in the grid and CSV (do not title-case addresses).
        Cell: ({ value }) => value || "-",
      },
      { 
        Header: "Phone",
        accessor: "phone",
        width: "15%",
        // Mobile takes precedence for dialling -- it is the number a person
        // actually answers -- while the column still shows what it always did.
        Cell: ({ value, row }) => (
          <PhoneCell
            value={value}
            dialNumber={row.original.mobile_number || value}
            parentTable="contacts"
            parentId={row.original.id}
            contactId={row.original.id}
            accountId={row.original.account_id}
          />
        )
      },
      {
        Header: "Primary Contact",
        accessor: "primary_contact",
        id: "primary_contact",
        width: "10%",
        Cell: ({ value }) =>
          value ? <StatusBadge status="open" label="Primary" /> : "-",
      },
      {
        Header: "Status",
        // API field is contact_status; transform also sets status for display/export parity.
        accessor: "status",
        width: "10%",
        Cell: ({ value, row }) => {
          const status = value || row.original.contact_status || "Active";
          return <StatusBadge status={status} label={status} />;
        },
      },
      {
        Header: "Action",
        accessor: "actions",
        width: "5%",
        disableSortBy: true,
        disableExport: true,
        Cell: ({ row }) => (
            <TableActionMenu
                row={row.original}
                onView={() => navigate(`/contacts/${row.original.id}`)}
                onDelete={isDeleting ? undefined : handleDeleteClick}
            />
        ),
      },
    ],
    rows: contacts,
  }), [contacts, isDeleting, navigate]);

  const handleCountryChange = (event) => {
    setNewContact({ ...newContact, mailing_country: event.target.value });
  };

  useEffect(() => {
    fetchAgents();
  }, []);
  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);
      console.log(`response.data for fetchAgents:`, response.data);

    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };


  const managers = agents;

  // if (isLoading) {
  //   return (
  //     <DashboardLayout>
  //       <DashboardNavbar />
  //       <DataLoadingSpinner 
  //         entityType="lead"
  //         title="Loading Contact Data"
  //         message="Please wait while we fetch the contact information..."
  //       />
  //       
  //     </DashboardLayout>
  //   );
  // }
  // Filter contacts by creation date (or updated_at if created_at is missing)
  const filterContactsByDate = (contacts, days) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return contacts.filter(contact => {
      const contactDate = new Date(contact.created_at || contact.updated_at);
      return contactDate >= cutoffDate;
    });
  };

  // Calculate percentage change between current and previous period
  const calculateContactGrowth = (contacts) => {
    // Current period (last 30 days)
    const currentContacts = filterContactsByDate(contacts, 30).length;

    // Previous period (30-60 days ago)
    const previousContacts = filterContactsByDate(
      contacts.filter(contact => {
        const contactDate = new Date(contact.created_at || contact.updated_at);
        const now = new Date();
        const past30Days = new Date(now);
        past30Days.setDate(now.getDate() - 30);
        const past60Days = new Date(now);
        past60Days.setDate(now.getDate() - 60);
        return contactDate >= past60Days && contactDate < past30Days;
      }),
      60
    ).length;

    // Calculate percentage change
    let percentageChange = 0;
    if (previousContacts > 0) {
      percentageChange = ((currentContacts - previousContacts) / previousContacts) * 100;
    } else if (currentContacts > 0) {
      percentageChange = 100; // No previous data, but current growth exists
    }

    return {
      percentageChange: Math.round(percentageChange),
      isPositive: percentageChange >= 0,
    };
  };
  const contactGrowth = useMemo(() => calculateContactGrowth(contacts), [contacts]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const filterContacts = (criteria) => {
    const filtered = contacts.filter(contact => {
      // Implement your filtering logic based on criteria
      return true; // Replace with actual condition
    });
    setFilteredContacts(filtered);
  };

  // Shared by the classic and v2 layouts below, so both render the same
  // statistics, actions and table.
  const kpiCards = [
    <ComplexStatisticsCard
      key="kpi-0"
      color="dark"
      icon="group"
      title="Total Contacts"
      count={contacts.length}
      percentage={{
        color: contactGrowth.isPositive ? "success" : "error",
        amount: `${contactGrowth.isPositive ? '+' : ''}${contactGrowth.percentageChange}%`,
        label: "than last month",
      }}
    />,
  ];
  const listActions = (
    <>
      <MDButton variant="gradient" color="dark" onClick={handleOpenModal}>
        <Icon sx={{ fontWeight: "bold" }}>add</Icon>
        &nbsp;Add Contact
      </MDButton>
    </>
  );
  const listBody = (
    <>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          {filteredContacts.length > 0 ? (
            <DataTable
              table={contactsTableData}
              isSorted={true}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20, 25],
              }}
              showTotalEntries={true}
              noEndBorder
              canSearch
              exportCSV={true}
              pagination={{ variant: "gradient", color: "info" }}
              objectType="contact"
              onFilterClear={() => {
                fetchContacts();
              }}
            />
          ) : (
            <EmptyState
              moduleName="Contact"
              canCreate={canCreate}
            />
          )}
        </>
      )}
    </>
  );

  const listLabel = app?.labels?.contacts || 'Contacts';
  const v2Main = (
    <MDBox>
      <PageHeader title={listLabel} summary={isLoading ? null : `${filteredContacts.length} ${listLabel.toLowerCase()}`} actions={listActions} />
      <Grid container spacing={2} mb={2.5}>
        {kpiCards.map((cardEl) => (
          <Grid item xs={12} sm={6} lg={3} key={cardEl.key}>
            {cardEl}
          </Grid>
        ))}
      </Grid>
      <Card>
        <MDBox pt={1}>{listBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              {kpiCards[0]}
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Contacts
                  </MDTypography>
                  {listActions}
                </MDBox>
                <MDBox pt={3}>
                  {listBody}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      )}

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%', // Adjusted for better responsiveness
          maxWidth: 500, // Maximum width limit
          maxHeight: '90vh', // Ensures it doesn't go beyond viewport height
          overflowY: 'auto', // Enables scrolling if content overflows
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <MDTypography id="modal-modal-title" variant="h6" component="h2" mb={2}>
            Add New Contact
          </MDTypography>
        </Box>
      </Modal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <ContactFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveContact}
        managers={managers}
        // accounts={accounts}
        user={user}
      />

      {/* Delete Confirmation Dialog */}

      <Dialog
          open={deleteConfirm.open}
          onClose={() => !isDeleting && setDeleteConfirm({ open: false, id: null })}
      >
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this Contact? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
              onClick={() => setDeleteConfirm({ open: false, id: null })}
              disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
              onClick={handleDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default Contacts;